import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { AlarmOption, PatientBehaviorDataModel, PatientBehaviorModel, PatientBehaviorType, PatientTargetDataModel, PatientTargetModel } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { ABCDataPanelComponent } from '../abc-data-panel/abc-data-panel.component';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-box-behavior-ratetimer',
  templateUrl: './box-behavior-ratetimer.component.html',
  standalone: false,
})
export class BoxBehaviorRatetimerComponent  extends BaseBoxComponent implements AfterViewInit, OnInit {
  @Input() text: string | undefined = '-';
  @Input() amountOfTimes: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() behaviorID: number = 0;
  @Input() Duration: number = 60;
  @Input() behavior: PatientBehaviorModel | undefined = undefined;
  @Input() isPreview?: boolean;
  timerInitialValue: number = 60;
  behaviorData: PatientBehaviorDataModel[] = [];
  abcData: PatientBehaviorDataModel[] = [];

  startDate: Date = new Date();

  behaviorType: PatientBehaviorType = PatientBehaviorType.RateTimer;

  @ViewChild('abcDataPanel') abcDataPanel!: ABCDataPanelComponent;

  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  intervals: { ID?: number, rate: number; RecordedDate: string; FormattedDate: string;}[] = [];  
  rate: number = 0;

  timeOut: any;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    this.isBehaviorBox = true; // Mark this as a behavior box to skip repositioning
    effect(() => {
      this.patientSessionService.dataChanged();
      const behaviorData = this.patientSessionService.getBehaviorData(this.behaviorID);
      if (behaviorData) {
        this.behaviorData = behaviorData.filter(t => !t.DeletedDate);
        this.intervals = this.behaviorData.map((t: PatientBehaviorDataModel) => {
          if (!t.TextValue) {
            t.TextValue = '';
          }
          this.rate = (t.IntValue ?? 0);
          return {
            ID: t.ID??undefined,
            rate: this.rate,
            RecordedDate: t.RecordedDate ?? '', 
            FormattedDate: this.formatFromToDate(new Date(t.RecordedDate ?? ''), new Date(t.EndDate ?? ''))
          };

        });

        
        
        this.amountOfTimes = this.intervals.length;
        
        this.abcData = this.behaviorData.filter((data) => data.Notes != null && data.Notes != undefined);

      }
      else {
        this.behaviorData = [];
        this.intervals = [];
      }
    });

    effect(() => {
      this.patientSessionService.dataChanged();
      const targetsRunningData = this.patientSessionService.getTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1,900000000+this.behaviorID);
      if (targetsRunningData) {
        const wasStarted = this.isStarted;
        this.isStarted = targetsRunningData.Data.isStarted;
        this.startTime = targetsRunningData.Data.startTime;
        this.timerValue = this.isStarted
          ? this.getCurrentTimerValue()
          : targetsRunningData.Data.timerValue;
        this.rate = (targetsRunningData.Data.rate ?? 0);
        if (this.isStarted && !wasStarted) {
          if (this.timeOut) {
            clearTimeout(this.timeOut);
          }
          this.timeOut = setTimeout(() => {
            this.updateTimer();
          }, 10);
        }
      } else {
        this.isStarted = false;
        this.startTime = 0;
        this.timerValue = this.timerInitialValue;
        this.rate = 0;
      }
    });
  }

  ngOnInit() {
    this.timerInitialValue = this.Duration ?? 60;
    this.timerValue = this.timerInitialValue;
  }

  override doLongPress(): void {
    this.showExtra2();
    setTimeout(() => {
      //scroll tabs to end
      const tabs = document.querySelectorAll('.abc-tabs');
      tabs.forEach(tab => {
        tab.scrollLeft = tab.scrollWidth;
      });
    }, 100);
  }
  

  startTimer() {
    if (this.isStarted) {
      this.showExtra();
    } else {
      this.isStarted = true;
      this.startTime = Date.now();
      this.startDate = new Date();
      this.rate = 0;
      this.updateTimer();
      this.saveTargetRunningData();
    }
  }


  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(
      this.patientSessionService.getPatientSession()?.ID ?? -1, 
      900000000+this.behaviorID, 
      { 
        isIntervalBehavior:true, 
        isStarted: this.isStarted, 
        startTime: this.startTime, 
        timerValue: this.timerValue, 
        rate: this.rate 
      });
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      const shouldRingAlarm = this.timerValue === 0 && 
        (this.behavior?.AlarmOption === AlarmOption.AlarmAtEnd);

      this.ringAlarmIfNeeded(
        this.behavior?.AlarmOption ?? AlarmOption.NoAlarm, 
        this.timerValue, 
        this.behavior?.Interval ?? 60, 
        this.behavior?.AlarmSoundOption ?? 1,
        false
      );

      if (this.timerValue <= 0) {
        // If alarm should ring, delay the stop to allow alarm to play
        if (shouldRingAlarm) {
          setTimeout(() => {
            this.stopTimer();
          }, 100);
        } else {
          this.stopTimer();
        }
      } else {
        if (this.timeOut) {
          clearTimeout(this.timeOut);
        }
        this.timeOut = setTimeout(() => {
          this.updateTimer();
        }, 1000);
      }
    }else {
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }
    }

  private getCurrentTimerValue(): number {
    return Math.max(
      0,
      this.timerInitialValue - Math.floor((Date.now() - this.startTime) / 1000)
    );
  }

  stopTimer(force: boolean = false) {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    this.isStarted = false;
    this.patientSessionService.addBehaviorData({
      IntValue: this.rate,
      TextValue: this.rate.toString(),
      PatientBehaviorID: this.behaviorID,
      RecordedDate: this.startDate.toServerString(),
      EndDate: new Date().toServerString(),
    });
    
      
    this.timerValue = this.timerInitialValue;
    this.rate = 0;
    this.startTime = Date.now();

    this.saveTargetRunningData();
    this.closeExtra();
    if(!force){
      this.startTimer();
    }
  }

  override ngOnDestroy() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    super.ngOnDestroy();
  }

  timerString() {
    const minutes = Math.floor(this.timerValue / 60);
    const seconds = this.timerValue % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  lastInterval() {
    if (this.intervals.length > 0) {
      const lastInterval = this.intervals[this.intervals.length - 1];
      return `${lastInterval.rate}`;
    } else {
      return '';
    }
  }

  increaseValue() {
    this.rate++;
    this.saveTargetRunningData();
  }

  setExtraBoxes(): void {
    const dropdowns = document.querySelectorAll('.p-overlay.p-component');
    //convert Element to ElementRef
    this.extraBoxes = Array.from(dropdowns).map(dropdown => new ElementRef(dropdown));
    this.extraBoxes.push(this.extraBox);
    this.extraBoxes.push(this.extraBox2);
  }

  addFirstABCData() {
    const currentDate = new Date();
    const formattedDate = this.formatDate(currentDate);
    let behaviorData: PatientBehaviorDataModel = {
      IntValue: 0,
      TextValue: '0',
      PatientBehaviorID: this.behaviorID,
      RecordedDate: currentDate.toServerString(),
    };
    this.patientSessionService.addBehaviorData(behaviorData);
    this.addABCData({ rate: 0, RecordedDate: currentDate.toServerString(), FormattedDate: formattedDate });
  }
  
  addABCData(interval: { ID?: number, rate: number, RecordedDate: string, FormattedDate: string }) {
    this.closeExtra2();
    this.abcDataPanel.initializeABCData(interval.RecordedDate);
    this.abcDataPanel.setEditingABCData(interval.RecordedDate);
    this.showExtra();
    setTimeout(() => {
      //scroll tabs to end
      const tabs = document.querySelectorAll('.abc-tabs');
      tabs.forEach(tab => {
        tab.scrollLeft = tab.scrollWidth;
      });
    }, 100);
  }

  hasABCData(interval: { ID?: number, rate: number, RecordedDate: string, FormattedDate: string }): boolean {
    return !!this.abcData.find(data => data.RecordedDate === interval.RecordedDate);
  }

  seeABCData(interval: { ID?: number, rate: number, RecordedDate: string, FormattedDate: string }) {
    this.closeExtra2();
    this.abcDataPanel.setEditingABCData(interval.RecordedDate);
    this.showExtra();
  }

  deleteData(data: any) {
    //confirm the deletion
    const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
    confirmed.subscribe(result => {
      if (result) {
        let behaviorDataToSave: PatientBehaviorDataModel | undefined = this.behaviorData.find(t =>
          t.RecordedDate === data.RecordedDate
        );

        if (behaviorDataToSave) {
          console.log('Updating existing anecdotal data:', behaviorDataToSave);
          behaviorDataToSave.DeletedDate = new Date().toServerString();

          this.patientSessionService.saveBehaviorData(behaviorDataToSave);
        }
      }
    });
  }
}
